// See more @ https://docs.sentry.io/platforms/javascript

declare global {
    interface Window {
        Sentry?: {
            captureMessage: (
                message: string,
                context?: {
                    level?:
                        'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
                    tags?: Record<
                        string,
                        | string
                        | number
                        | boolean
                        | bigint
                        | symbol
                        | null
                        | undefined
                    >
                    extra?: Record<
                        string,
                        string | number | boolean | null | undefined
                    >
                }
            ) => void
        }
    }
}

type LivewireRequestRejection = {
    status: number | null
    body: string | null
    json: object | null
    errors: object | null
}

export type SentryEventLike = {
    exception?: {
        values?: Array<{ value?: string }>
    }
    extra?: {
        __serialized__?: LivewireRequestRejection
    }
    tags?: Record<
        string,
        string | number | boolean | bigint | symbol | null | undefined
    >
}

export function sentryBeforeSend<T extends SentryEventLike>(
    event: T
): T | null {
    const message = event.exception?.values?.[0]?.value ?? ''

    if (/csrf token mismatch/i.test(message)) {
        return null
    }

    if (/networkerror when attempting to fetch resource/i.test(message)) {
        return null
    }

    if (/failed to fetch dynamically imported module/i.test(message)) {
        return null
    }

    if (isCancelledLivewireRequest(event)) {
        return null
    }

    return tagLivewireRejectionStatus(event)
}

// Every Livewire rejection reports under the same wording, which names its keys
// and not what failed: the status is what tells a session expiry from a 500.
function tagLivewireRejectionStatus<T extends SentryEventLike>(event: T): T {
    const status = event.extra?.__serialized__?.status

    if (!status) {
        return event
    }

    event.tags = { ...event.tags, 'livewire.status': String(status) }

    return event
}

// Livewire rejects its action promises with { status, body, json, errors }. All four
// null means the request was cancelled or never answered, which a poll on an
// abandoned page does routinely: a real HTTP failure carries its status.
function isCancelledLivewireRequest<T extends SentryEventLike>(
    event: T
): boolean {
    const rejection = event.extra?.__serialized__

    if (!rejection) {
        return false
    }

    return (
        !rejection.status
        && !rejection.body
        && !rejection.json
        && !rejection.errors
    )
}

class SentryVendor {
    static init: () => Promise<any> = async () => {
        if (!window.shared.sentry.dsn) {
            return await Promise.resolve()
        }

        // Config
        const Sentry = await import('@sentry/browser')
        window.Sentry = Sentry as typeof window.Sentry

        const sampleRate = window.shared.sentry.traces_sample_rate
            ? parseFloat(window.shared.sentry.traces_sample_rate)
            : 0.0

        // Sentry init
        Sentry.init({
            enabled: !!window.shared.sentry.dsn,
            dsn: window.shared.sentry.dsn,
            environment: window.shared.app.env,
            sampleRate,
            integrations: [Sentry.browserTracingIntegration()],
            dataCollection: { userInfo: true },
            denyUrls: [
                /extensions\//i,
                /^chrome-extension:\/\//i,
                /^moz-extension:\/\//i,
            ],
            beforeSend: sentryBeforeSend,
        })

        Sentry.setContext('user', {
            id: window.shared.auth.id,
            name: window.shared.auth.name,
            email: window.shared.auth.email,
        })

        return await Promise.resolve()
    }
}

export default SentryVendor
